import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as CheckCircle } from './icons/check_circle.svg';
import useFeedbackState, { defaultValue, FeedbackState } from './useFeedbackState';
import Input from '@gr-monorepo/common/ui/Input';
import Textarea from '@gr-monorepo/common/ui/Textarea';
import { Button, Space } from '@gr-monorepo/common/ui';
import { FeedbacksApi } from 'api';
import { FieldFlexRow, PolicyLink, SuccessWrap } from './FeedbackStyled';
import { getFeedbackStorage, setFeedbackStorage } from './SessionStorage';
import { LogClick, useClickStreamContext } from '../ClickStream';
import { FeedbackFileUpload } from './components/FeedbackFileUpload';
import useFormState from '@gr-monorepo/common/utils/useFormState';

const SuccessSubmit = () => {
  return <CheckCircle />;
};
function testEmail(email: string) {
  if (/[А-Яа-я]/.test(email)) {
    return 'Русские буквы недопустимы';
  }
  if (/\s/.test(email)) {
    return 'Пробелы недопустимы';
  }
  if (!/\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/.test(email)) {
    return 'Проверьте правильность email';
  }
}

function testPhone(phone: string) {
  if (phone.replace(/[^\d]/g, '').length !== 11) {
    return 'Введите 10 цифр';
  }
}
const validateForm = (formState: FeedbackState) => {
  let isValid = true;
  if (!formState.values.fio) {
    isValid = false;
    formState.setError('fio')('Обязательное поле');
  }
  if (!formState.values.workplacePosition) {
    isValid = false;
    formState.setError('workplacePosition')('Обязательное поле');
  }
  if (!formState.values.phone && !formState.values.email) {
    isValid = false;
    formState.setError('email')('Обязательное поле');
    formState.setError('phone')(' ');
  } else {
    if (formState.values.phone) {
      const result = testPhone(formState.values.phone);
      if (result) {
        formState.setError('phone')(result || null);
        isValid = false;
      }
    }
    if (formState.values.email) {
      const result = testEmail(formState.values.email);
      if (result) {
        formState.setError('email')(result || null);
        isValid = false;
      }
    }
  }

  if (!formState.values.text) {
    isValid = false;
    formState.setError('text')('Обязательное поле');
  }
  return isValid;
};

const FeedbackForm = (props: {}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const formState = useFeedbackState();
  const attachmentsState = useFormState<{ isSendDisabled: boolean; attachIds: number[] }>({
    isSendDisabled: false,
    attachIds: [],
  });
  const { logPageEvent } = useClickStreamContext();
  const fileUploadRef = useRef<{ removeFilesCallback: () => void }>();
  const onSubmit = async () => {
    const result = validateForm(formState);
    if (!result) return;
    const { workplacePosition, ...value } = formState.values;

    try {
      await FeedbacksApi.createFeedback({
        ...value,
        workplace_position: workplacePosition,
        attachment_ids: attachmentsState.values.attachIds,
      });
      logPageEvent({
        eventType: 'feedback_sent',
        eventInfo: `Отправлена ОС`,
      });
    } catch (e) {
      logPageEvent({
        eventType: 'feedback_sent_error',
        eventInfo: `Ошибка отравки обратно связи`,
      });
      alert('Ошибка');
      return;
    }

    formState.setValues({ ...defaultValue });
    fileUploadRef.current?.removeFilesCallback();
    setFeedbackStorage({ ...defaultValue });
    setIsSuccess(true);
  };

  const saveValues = () => {
    setFeedbackStorage(formState.values);
  };

  useEffect(() => {
    const values = getFeedbackStorage();
    if (values) formState.setValues(values);
  }, []);

  if (isSuccess)
    return (
      <>
        <SuccessWrap>
          <SuccessSubmit />

          <div style={{ fontSize: 30, marginTop: 39 }}>Спасибо за обращение!</div>
        </SuccessWrap>
      </>
    );
  return (
    <>
      Как вас зовут*
      <Space size={12} axis="vertical" />
      <Input
        value={formState.values.fio}
        onChange={formState.setValue('fio')}
        error={!!formState.errors.fio}
        errorText={formState.errors.fio || ''}
        placeholder="Фамилия Имя Отчество"
        onBlur={saveValues}
        style={{ borderColor: !!formState.errors.fio ? '#FF6170' : 'rgba(9, 21, 38, 0.6)' }}
      />
      <Space size={24} axis="vertical" />
      Кем вы работаете*
      <Space size={12} axis="vertical" />
      <Input
        value={formState.values.workplacePosition}
        onChange={formState.setValue('workplacePosition')}
        error={!!formState.errors.workplacePosition}
        errorText={formState.errors.workplacePosition || ''}
        placeholder="Ваша должность"
        onBlur={saveValues}
        style={{ borderColor: !!formState.errors.workplacePosition ? '#FF6170' : 'rgba(9, 21, 38, 0.6)' }}
      />
      <Space size={24} axis="vertical" />
      Как с вами связаться*
      <Space size={12} axis="vertical" />
      <FieldFlexRow>
        <Input
          value={formState.values.email}
          onChange={formState.setValue('email')}
          error={!!formState.errors.email}
          errorText={formState.errors.email || ''}
          placeholder="email@pochta.ru"
          style={{ width: '100%', borderColor: !!formState.errors.email ? '#FF6170' : 'rgba(9, 21, 38, 0.6)' }}
          onBlur={saveValues}
        />
        <Space size={16} />
        или
        <Space size={16} />
        <Input
          value={formState.values.phone}
          onChange={formState.setValue('phone')}
          error={!!formState.errors.phone}
          errorText={formState.errors.phone || ''}
          placeholder="+7 (XXX) XXX-XX-XX"
          style={{ width: '100%', borderColor: !!formState.errors.phone ? '#FF6170' : 'rgba(9, 21, 38, 0.6)' }}
          mask="+7 (000) 000-00-00"
          onBlur={saveValues}
        />
      </FieldFlexRow>
      <Space size={24} axis="vertical" />
      Ваше предложение или вопрос*
      <Space size={12} axis="vertical" />
      <Textarea
        value={formState.values.text}
        onChange={formState.setValue('text')}
        error={!!formState.errors.text}
        errorText={formState.errors.text || ''}
        placeholder="Напишите текст и при необходимости прикрепите файлы"
        onBlur={saveValues}
        style={{ height: 218, borderColor: !!formState.errors.text ? '#FF6170' : 'rgba(9, 21, 38, 0.6)' }}
      />
      <Space size={24} axis="vertical" />
      Поля, помеченные *, обязательны для заполнения
      <Space size={32} axis="vertical" />
      <FeedbackFileUpload
        isErrorOrLoading={attachmentsState.setValue('isSendDisabled')}
        onListUpdate={attachmentsState.setValue('attachIds')}
        ref={fileUploadRef}
      />
      <Space size={4} axis="vertical" />
      Нажимая «Отправить», вы соглашаетесь с{' '}
      <LogClick eventType="feedback_open_privacy" eventInfo={`Нажал открыть политику обработки ПД в обратной связи`}>
        <PolicyLink href="https://uchi.ru/privacy" target="_blank">
          политикой обработки персональных данных
        </PolicyLink>{' '}
      </LogClick>
      <Space size={16} axis="vertical" />
      <Button
        onClick={onSubmit}
        style={{ width: '100%' }}
        color="green"
        colorDisabled={'#D9D9D9'}
        isDisabled={attachmentsState.values.isSendDisabled}
      >
        ОТПРАВИТЬ
      </Button>
    </>
  );
};

export default FeedbackForm;
